import { useState, useEffect } from 'react';
import { Right, Role } from '../types/roles';
import dataProvider from '../../services/api';

export const useRights = (
  selectedRole: Role | null,
  showSuccessMessage: (message: string) => void,
  handleErrorWithLogout: (error: any) => void
) => {
  const [allRights, setAllRights] = useState<Right[]>([]);
  const [availableRights, setAvailableRights] = useState<Right[]>([]);
  const [assignedRights, setAssignedRights] = useState<Right[]>([]);
  const [selectedAvailableRights, setSelectedAvailableRights] = useState<string[]>([]);
  const [selectedAssignedRights, setSelectedAssignedRights] = useState<string[]>([]);

  const fetchAllRights = async () => {
    try {
      const { data: rightsData } = await dataProvider.getAllRights('rights');
      setAllRights(rightsData);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setAllRights([]);
    }
  };

  const fetchAssignedRights = async () => {
    if (!selectedRole) {
      setAssignedRights([]);
      setAvailableRights(allRights);
      return;
    }

    try {
      const { data: assignedRightsData } = await dataProvider.getAssignedRights('roles', {
        id: selectedRole.id,
      });
      setAssignedRights(assignedRightsData);
      const assignedRightIds = new Set(assignedRightsData.map((right: Right) => right.id));
      const updatedAvailableRights = allRights.filter((right) => !assignedRightIds.has(right.id));
      setAvailableRights(updatedAvailableRights);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setAssignedRights([]);
      setAvailableRights(allRights);
    }
  };

  const moveToAssigned = async (rightsToMove: Right[]) => {
    if (!selectedRole) return;

    const previousAssignedRights = [...assignedRights];
    const previousAvailableRights = [...availableRights];

    try {
      const currentAssignedRights = [...assignedRights];
      const updatedAssignedRights = [...currentAssignedRights, ...rightsToMove];
      const uniqueAssignedRights = Array.from(new Set(updatedAssignedRights.map((r) => r.id))).map((id) =>
        updatedAssignedRights.find((r) => r.id === id)!
      );

      setAssignedRights(uniqueAssignedRights);
      setAvailableRights(availableRights.filter((right) => !rightsToMove.some((r) => r.id === right.id)));
      setSelectedAvailableRights([]);

      const rightIds = uniqueAssignedRights.map((right) => right.id);
      await dataProvider.assignRights('roles', {
        id: selectedRole.id,
        rightIds,
      });
      showSuccessMessage('Rights assigned successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
      setAssignedRights(previousAssignedRights);
      setAvailableRights(previousAvailableRights);
    }
  };

  const moveToAvailable = async (rightsToMove: Right[]) => {
    if (!selectedRole) return;

    const previousAssignedRights = [...assignedRights];
    const previousAvailableRights = [...availableRights];

    try {
      const currentAssignedRights = [...assignedRights];
      const updatedAssignedRights = currentAssignedRights.filter(
        (right) => !rightsToMove.some((r) => r.id === right.id)
      );
      const updatedAvailableRights = [...availableRights, ...rightsToMove];

      setAssignedRights(updatedAssignedRights);
      setAvailableRights(updatedAvailableRights);
      setSelectedAssignedRights([]);

      const rightIds = updatedAssignedRights.map((right) => right.id);
      await dataProvider.assignRights('roles', {
        id: selectedRole.id,
        rightIds,
      });
      showSuccessMessage('Rights removed successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
      setAssignedRights(previousAssignedRights);
      setAvailableRights(previousAvailableRights);
    }
  };

  useEffect(() => {
    fetchAssignedRights();
  }, [selectedRole, allRights]);

  return {
    allRights,
    availableRights,
    setAvailableRights,
    assignedRights,
    setAssignedRights,
    selectedAvailableRights,
    setSelectedAvailableRights,
    selectedAssignedRights,
    setSelectedAssignedRights,
    fetchAllRights,
    fetchAssignedRights,
    moveToAssigned,
    moveToAvailable,
  };
};